// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import React, { useState, useEffect } from 'react';
import ProgressBar from './ProgressBar';
import Tooltip from './Tooltip';
import './FuelGauge.css';
import { useFuel } from '../contexts/FuelContext';

/**
 * FuelGauge Component
 * Displays antimatter fuel level with color-coded status and liquid fill effect
 */
const FuelGauge = ({ fuelLevel, vertical = false, showConsumptionRate = false }) => {
  const { maxFuel } = useFuel();
  const [previousFuel, setPreviousFuel] = useState(fuelLevel);
  const [consumptionRate, setConsumptionRate] = useState(0);
  const [isConsuming, setIsConsuming] = useState(false);
  
  // TODO: Tank upgrades will come from UpgradesContext when created
  const tankUpgrades = 0; // Temporary until UpgradesContext is ready
  
  // Calculate actual max fuel including tank upgrades (each tank adds 25%)
  const actualMaxFuel = maxFuel * (1 + (tankUpgrades * 0.25));
  
  // Calculate fuel percentage for color coding
  const fuelPercentage = (fuelLevel / actualMaxFuel) * 100;
  
  // Get fuel status color
  const getFuelColor = () => {
    if (fuelPercentage >= 60) return '#00ffff'; // Cyan - good
    if (fuelPercentage >= 30) return '#ffaa00'; // Orange - warning
    return '#ff4444'; // Red - critical
  };
  
  // Track fuel consumption rate
  useEffect(() => {
    if (showConsumptionRate) {
      const fuelDiff = previousFuel - fuelLevel;
      if (fuelDiff > 0) {
        setConsumptionRate(fuelDiff);
        setIsConsuming(true);
        
        // Reset consuming state after 2 seconds
        const timeout = setTimeout(() => {
          setIsConsuming(false);
        }, 2000);
        
        return () => clearTimeout(timeout);
      }
      setPreviousFuel(fuelLevel);
    }
  }, [fuelLevel, previousFuel, showConsumptionRate]);

  return (
    <div className="fuel-gauge-container">
      <Tooltip
        text="If fuel is depleted mid-travel, an emergency tow will transport you to the nearest refuel station."
        position="top"
      >
        <div className="fuel-header">
          <div className="fuel-label">
            ANTIMATTER FUEL
            {tankUpgrades > 0 && (
              <span className="tank-upgrade-badge">
                [+{tankUpgrades} TANK{tankUpgrades > 1 ? 'S' : ''}]
              </span>
            )}
          </div>
          <div className="fuel-value" style={{ color: getFuelColor() }}>
            {fuelLevel.toFixed(1)}% / {actualMaxFuel.toFixed(0)}%
          </div>
        </div>
      </Tooltip>
      
      {/* Enhanced Progress Bar with Liquid Effect */}
      <div className="fuel-bar-container">
        <ProgressBar 
          value={fuelLevel}
          max={actualMaxFuel}
          color="auto"
          showLabel={false}
          animated={false}
          size="small"
        />
        
        {/* Liquid wave effect overlay */}
        <div 
          className="fuel-liquid-wave"
          style={{
            width: `${(fuelLevel / actualMaxFuel) * 100}%`,
            background: getFuelColor()
          }}
        />
      </div>
      
      {/* Consumption Rate Indicator */}
      {showConsumptionRate && isConsuming && consumptionRate > 0 && (
        <div className="fuel-consumption-indicator">
          <span className="consumption-icon">⚡</span>
          <span className="consumption-text">
            -{consumptionRate.toFixed(2)}%/s
          </span>
        </div>
      )}
      
      {/* Low Fuel Warning */}
      {fuelPercentage < 20 && (
        <div className="fuel-warning-indicator">
          <span className="warning-icon pulse-animation">⚠️</span>
          <span className="warning-text">LOW FUEL WARNING</span>
        </div>
      )}
    </div>
  );
};

export default FuelGauge;
